/**
 * @fileoverview 404 — Page Not Found.
 *
 * Replaces the previous silent `<Navigate to="/dashboard" />` fallback so an
 * admin who lands on a mistyped URL (or follows a stale link in a
 * notification / shared message) sees a clear explanation instead of
 * being teleported to the dashboard with no feedback.
 *
 * The page is intentionally calm — no large illustration, no animation —
 * so a misclick doesn't feel like an error. Two paths forward: back to
 * the dashboard, or back in browser history when the user got here via
 * a navigation we can rewind.
 */
import { useLocation, useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Home, ArrowLeft } from 'lucide-react'

export function NotFoundPage() {
  const location = useLocation()
  const navigate = useNavigate()

  // Only offer "Go Back" when there's actually history to rewind to.
  // Otherwise it would either no-op or send the user out of the app
  // entirely (e.g. into a previously-visited external page).
  const canGoBack = typeof window !== 'undefined' && window.history.length > 1

  return (
    <div className="flex min-h-[60vh] items-center justify-center px-6">
      <div className="max-w-md text-center">
        <p className="text-7xl font-bold tracking-tight text-emerald-600 dark:text-emerald-500">
          404
        </p>
        <h1 className="mt-4 text-2xl font-semibold">Page not found</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          We couldn&rsquo;t find a page at{' '}
          <code className="rounded bg-muted px-1.5 py-0.5 text-xs">
            {location.pathname}
          </code>
          . The link might be old, or you may have typed the URL by hand.
        </p>
        <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
          {canGoBack && (
            <Button
              variant="outline"
              onClick={() => navigate(-1)}
              className="gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Go back
            </Button>
          )}
          <Button onClick={() => navigate('/dashboard')} className="gap-2">
            <Home className="h-4 w-4" />
            Go to dashboard
          </Button>
        </div>
      </div>
    </div>
  )
}
